import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { faPlay } from '@fortawesome/pro-solid-svg-icons/faPlay';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { observer } from 'mobx-react-lite';
import { useSearchParams } from 'react-router-dom';

import {
  namespaces,
  useSharedTranslation,
} from 'us-react-components/dist/hooks/useSharedTranslation';
import { LogoSpinner } from 'us-react-components/dist/LogoSpinner/LogoSpinner';
import { ProfileVideoModal } from 'us-react-components/dist/ProfileVideoModal/ProfileVideoModal';
import {
  axios,
  SampleVideo,
  SampleVideoService,
  VideoService,
} from 'us-web-services';

import { Button, ButtonType } from '../../components';
import { useDependencies } from '../../dependencies';

const MAX_VIDEO_SIZE = 52428800; // 50MB
const VIMEO_CREATE_URL = 'https://api.vimeo.com/me/videos';
const VIDEO_FAQ_URL =
  'https://support.urbansitter.com/hc/en-us/articles/200337838-How-to-record-and-upload-a-profile-video';

type Step = 'upload' | 'uploading' | 'finished' | 'error';

export const VideoUpload = observer((): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const [searchParams, setSearchParams] = useSearchParams();
  const { stores } = useDependencies();
  const user = stores.user.user;
  const isNux = searchParams.get('nux') === 'true';

  const [step, setStep] = useState<Step>('upload');
  const [errorText, setErrorText] = useState<string>('');
  const [fileErrorText, setFileErrorText] = useState<string>('');
  const [filename, setFilename] = useState<string>('');
  const [sampleVideos, setSampleVideos] = useState<SampleVideo[]>([]);
  const [playingVideo, setPlayingVideo] = useState<SampleVideo | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async (): Promise<void> => {
      try {
        const resp = await SampleVideoService.getByFilter();
        if (cancelled) return;
        const list = resp.data?.data ?? [];
        if (list.length >= 2) setSampleVideos(list);
      } catch (err) {
        console.warn('[VideoUpload] sample-videos load failed', err);
      } finally {
        if (!cancelled) setIsLoaded(true);
      }
    };
    void load();
    return (): void => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    document.title = 'UrbanSitter - Upload Video';
  }, []);

  useEffect(() => {
    if (!user?.id) return;
    const videoUri = searchParams.get('video_uri');
    if (!videoUri) return;

    const matches = /\/videos\/(\d+)/i.exec(videoUri);
    if (!matches) {
      setErrorText(t('pages.videoUpload.unexpectedProcessError'));
      setStep('error');
      setSearchParams((sp) => {
        const next = new URLSearchParams(sp);
        next.delete('video_uri');
        return next;
      }, { replace: true });
      return;
    }

    void (async (): Promise<void> => {
      try {
        await VideoService.create({ vimeoUri: videoUri, userId: user.id });
        setStep('finished');
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.data?.errors?.[0]?.code === 'video.duplicate') {
          setErrorText(t('pages.videoUpload.duplicateError'));
        } else {
          setErrorText(t('pages.videoUpload.saveError'));
        }
        setStep('error');
      } finally {
        setSearchParams((sp) => {
          const next = new URLSearchParams(sp);
          next.delete('video_uri');
          return next;
        }, { replace: true });
      }
    })();

  }, [user?.id]);

  const onFileChange = async (e: ChangeEvent<HTMLInputElement>): Promise<void> => {
    setErrorText('');
    setFileErrorText('');
    const file = e.target.files?.[0];
    if (!file) {
      setFileErrorText(t('pages.videoUpload.troubleOpening'));
      return;
    }
    setFilename(file.name);
    if (file.size > MAX_VIDEO_SIZE) {
      setFileErrorText(t('pages.videoUpload.sizeLimit'));
      return;
    }
    if (!file.type.toLowerCase().includes('video')) {
      setFileErrorText(t('pages.videoUpload.notSupported', { name: file.name }));
      return;
    }

    await startUpload(file);
  };

  const startUpload = async (file: File): Promise<void> => {
    const vimeoToken = process.env.REACT_APP_VIMEO_TOKEN;
    if (!vimeoToken) {
      setErrorText(t('pages.videoUpload.saveError'));
      setStep('error');
      return;
    }

    setStep('uploading');

    try {
      const createResp = await fetch(VIMEO_CREATE_URL, {
        method: 'POST',
        headers: {
          Accept: 'application/vnd.vimeo.*+json;version=3.4',
          Authorization: `Bearer ${vimeoToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          upload: {
            approach: 'post',
            redirect_url: window.location.href,
            size: file.size,
          },
          name: user?.firstName ?? '',
        }),
      });
      if (!createResp.ok) {
        let json: { errors?: Array<{ message?: string }> } = {};
        try {
          json = await createResp.json();
        } catch {
          /* ignore */
        }
        setErrorText(json?.errors?.[0]?.message ?? t('pages.videoUpload.uploadError'));
        setStep('error');
        return;
      }
      const data = await createResp.json();
      const uploadLink = data?.upload?.upload_link;
      if (!uploadLink) {
        setErrorText(t('pages.videoUpload.unexpectedUploadError'));
        setStep('error');
        return;
      }

      // Vimeo `approach: 'post'` expects a real multipart form POST and
      // responds with a redirect to redirect_url?video_uri=...; a fetch()
      // upload would either be blocked by CORS or never trigger the
      // browser navigation that delivers `video_uri` back to this page.
      const form = formRef.current;
      if (!form) {
        setErrorText(t('pages.videoUpload.unexpectedUploadError'));
        setStep('error');
        return;
      }
      form.action = uploadLink;
      form.submit();
    } catch {
      setErrorText(t('pages.videoUpload.uploadError'));
      setStep('error');
    }
  };

  const reset = (): void => {
    setStep('upload');
    setErrorText('');
    setFileErrorText('');
    setFilename('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const cancelUpload = (): void => {
    window.location.reload();
  };

  const finishedHref = isNux ? '/app/job-board?nux=true' : '/app/caregiver/dashboard';
  const finishedLabel = isNux
    ? t('pages.videoUpload.toJobBoard')
    : t('pages.videoUpload.toDashboard');

  if (!isLoaded) {
    return (
      <div className='container py-8 text-center'>
        <i className='fa-solid fa-spinner fa-spin mr-2' />
        <span>{t('common.loading')}</span>
      </div>
    );
  }

  return (
    <div className='container py-8 max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8'>
      <div className='lg:col-span-2'>
      <h1 className='text-2xl font-semibold mb-4'>{t('pages.videoUpload.heading')}</h1>

      {step === 'upload' && (
        <>
          <p className='text-sm mb-6'>{t('pages.videoUpload.prompt')}</p>

          <div className='us-card rounded p-5 mb-6 space-y-5 text-sm'>
            <div>
              <h3 className='font-semibold mb-2'>{t('pages.videoUpload.step1Heading')}</h3>
              <ul className='list-disc pl-5 space-y-1'>
                <li>{t('pages.videoUpload.step1Bullet1')}</li>
                <li>{t('pages.videoUpload.step1Bullet2')}</li>
              </ul>
            </div>
            <div>
              <h3 className='font-semibold mb-2'>{t('pages.videoUpload.step2Heading')}</h3>
              <ul className='list-disc pl-5 space-y-1'>
                <li>{t('pages.videoUpload.step2Bullet1')}</li>
                <li>{t('pages.videoUpload.step2Bullet2')}</li>
                <li>{t('pages.videoUpload.step2Bullet3')}</li>
                <li>{t('pages.videoUpload.step2Bullet4')}</li>
              </ul>
            </div>
            <div>
              <h3 className='font-semibold mb-2'>{t('pages.videoUpload.step3Heading')}</h3>
              <ul className='list-disc pl-5 space-y-1'>
                <li>{t('pages.videoUpload.step3Bullet1')}</li>
                <li>{t('pages.videoUpload.step3Bullet2')}</li>
                <li>{t('pages.videoUpload.step3Bullet3')}</li>
              </ul>
              <p className='mt-3'>
                {t('pages.videoUpload.videoFaqPrefix')}{' '}
                <a
                  href={VIDEO_FAQ_URL}
                  target='_blank'
                  rel='noreferrer'
                  className='underline'
                >
                  {t('pages.videoUpload.videoFaqLink')}
                </a>
              </p>
            </div>
          </div>
        </>
      )}

      {/* Form must remain mounted across step changes: setStep('uploading')
          fires before the Vimeo create-upload fetch resolves, and unmounting
          would null out formRef and drop the file input's selected file. */}
      <form
        ref={formRef}
        id='upload-form'
        action=''
        method='post'
        encType='multipart/form-data'
        className={step === 'upload' ? '' : 'hidden'}
      >
        <label
          htmlFor='video-file'
          className='inline-block bg-sapphire text-white px-4 py-2 rounded cursor-pointer'
        >
          <span className='hidden sm:inline'>{t('pages.videoUpload.chooseFile')}</span>
          <span className='sm:hidden'>{t('pages.videoUpload.chooseFileMobile')}</span>
          <input
            id='video-file'
            ref={fileInputRef}
            type='file'
            name='file_data'
            accept='video/*'
            onChange={onFileChange}
            className='hidden'
          />
        </label>
      </form>
      {step === 'upload' && filename && (
        <span className='ml-3 text-sm text-neutral-700'>{filename}</span>
      )}
      {step === 'upload' && fileErrorText && (
        <p className='text-sm text-red-600 mt-3'>{fileErrorText}</p>
      )}

      {step === 'uploading' && (
        <div className='text-center'>
          <div className='mb-4'>
            <LogoSpinner style={{ height: 40, width: 40, marginBottom: 2 }} />
          </div>
          <p className='text-neutral-700 mb-4'>
            {t('pages.videoUpload.uploading', { filename })}
          </p>
          <Button buttonType={ButtonType.secondary} onClick={cancelUpload}>
            {t('pages.videoUpload.cancelUpload')}
          </Button>
        </div>
      )}

      {step === 'finished' && (
        <>
          <h3 className='text-xl font-semibold mb-3'>
            {t('pages.videoUpload.finishedHeading')}
          </h3>
          <p className='text-neutral-700 mb-3'>{t('pages.videoUpload.finishedBody')}</p>
          <p className='text-neutral-700 mb-5'>{t('pages.videoUpload.finishedBody2')}</p>
          <Button
            buttonType={ButtonType.primary}
            onClick={(): void => {
              window.location.href = finishedHref;
            }}
          >
            {finishedLabel}
          </Button>
        </>
      )}

      {step === 'error' && (
        <>
          <h3 className='text-xl font-semibold mb-3'>
            {t('pages.videoUpload.errorHeading')}
          </h3>
          <p className='text-red-600 mb-4'>{errorText || t('pages.videoUpload.genericError')}</p>
          <div className='flex gap-3'>
            <Button buttonType={ButtonType.primary} onClick={reset}>
              {t('pages.videoUpload.tryAgain')}
            </Button>
            <Button
              buttonType={ButtonType.secondary}
              onClick={(): void => {
                window.location.href = '/app/caregiver/dashboard';
              }}
            >
              {t('pages.videoUpload.goToDashboard')}
            </Button>
          </div>
        </>
      )}
      </div>

      {sampleVideos.length >= 2 && (
        <aside id='sitter-video-sidebar' className='text-center'>
          <h2 className='font-semibold text-lg mb-2'>
            {t('pages.videoUpload.sampleVideosHeading')}
          </h2>
          <p className='mx-8 mb-4 text-sm'>
            {t('pages.videoUpload.sampleVideosBody')}
          </p>
          {sampleVideos.slice(0, 2).map((video, i) => (
            <div key={video.id ?? i} className={`video ${i === 0 ? 'mb-5' : ''}`}>
              <Button
                buttonType={ButtonType.tertiary}
                aria-label={`Play Sample Video ${i + 1}`}
                id={`play-video-${video.id ?? i}`}
                data-tracking-category='Sitter Video Upload'
                data-tracking-label={`Sample Video ${i + 1}`}
                onClick={(): void => {
                  if (video.vimeoId) setPlayingVideo(video);
                }}
                className='block w-full cursor-pointer relative'
              >
                <div className='absolute inset-0 flex items-center justify-center'>
                  <span className='bg-black/50 rounded-full w-14 h-14 flex items-center justify-center'>
                    <FontAwesomeIcon icon={faPlay} className='text-white text-xl ml-1' />
                  </span>
                </div>
                <img
                  src={video.thumbnail}
                  alt={`Sample video ${i + 1} thumbnail`}
                  className='w-full'
                />
                <div className='font-medium pt-2'>
                  {video.title} &ndash; {video.duration ?? (i === 0 ? 28 : 24)} seconds
                </div>
              </Button>
            </div>
          ))}
        </aside>
      )}

      <ProfileVideoModal
        isOpen={playingVideo !== null}
        onClose={(): void => setPlayingVideo(null)}
        vimeoId={playingVideo?.vimeoId}
        title={playingVideo?.title}
      />
    </div>
  );
});
